import { FACT_RESULT } from './facts.js';
import { policyNorm, rowCarrierText, sameCarrier } from './miss_evidence/carrier.js';
import { fieldsNamed } from './miss_evidence/fields.js';
import { policyFindings, resolveBetterPolicy } from './miss_evidence/policy_finder.js';
import { classifyPolicy, wrongReason, WRONG_WORDS } from './policy_match.js';
import { realClaim } from './ready_trend.js';
import { controllerReason, FAILURE_TEXT, failureReason } from './reasons.js';
import { sweepStateFarm } from './sweep_carrier.js';
import { dolLabel, oneWorkingDayAfter, pacificDay, pacificStamp } from './time.js';
import { inGrace, isParked, todoGroup } from './todo.js';
import { isIssueRow } from './urgent.js';
export const FOUND_WINDOW_DAYS = 30;
/** the controller holds a new number must not override (PR 3 plan, Global Constraints), less the three caps (D3, Cesar 10-29: a cap never hides
 *  Approve; the approved call's cap kind is the weekly one only, controller verify.js) */
export const FOUND_HOLDS = new Set(['CARRIER_REQUIRES_LIVE_PERSON', 'HUMAN_ONLY_CARRIER', 'CHANNEL_REFUSED', 'REFUSED_AI_RECENT',
    'AI_REFUSED_ON_RECORD', 'STATE_FARM_PORTAL', 'KILL_SWITCH', 'ACTION_OFF', 'TWIN_OF_OPENED', 'OUT_OF_SCOPE', 'COMPANION_SECOND_CLIENT']);
/** D3: Approve stays on a row whose record waits for this week's call limit; the row says so */
export const FOUND_CAP_WAIT_WORDS = "Three calls already this week: Approve now, the call waits for the weekly limit to free";
const DAY = 86_400_000;
const DOC_LABEL = { card: 'insurance card', dec_page: 'dec page', police_report: 'police report' };
const ms = (s) => (s ? Date.parse(String(s).replace(/([+-]\d{2})(\d{2})$/, '$1:$2')) : NaN);
const k15 = (id) => String(id ?? '').slice(0, 15);
const sideOfRequest = (s) => (s === 'PLT' ? 'PLT' : s === 'DEF' || s === 'Other Party' ? 'DEF' : null);
const inWindow = (at, now) => { const age = now.getTime() - ms(at); return age >= 0 && age <= FOUND_WINDOW_DAYS * DAY; };
/** a dialed miss on an insurance record whose reason is policy not found, or missing information that names the policy (any age) */
export function qualifiesAny(r) {
    if (r.dialed !== true || r.result !== 'miss' || !r.insuranceId || r.insuranceId.startsWith('gap:'))
        return false;
    return r.reason === 'notFound' || (r.reason === 'missing' && fieldsNamed(r.missReason).includes('policy'));
}
/** ... of the last 30 days */
export function qualifies(r, now) { return qualifiesAny(r) && inWindow(r.at, now); }
/** The record's approval among its policy-number facts entered after `afterMs` (final review 10-29, I2): a fact settled APPLIED as
 *  ALREADY_ON_RECORD that follows an APPLIED fact is a second press of the same approval (the earlier one stands; controller approved_call.js
 *  approvalOf, which also compares value_hash: the console's read has none). Otherwise the latest fact. */
export function approvalFor(facts, insurance15, afterMs) {
    const xs = facts.filter((f) => f.kind === 'policy_number' && k15(f.insurance_id) === insurance15 && ms(f.entered_at) > afterMs)
        .sort((a, b) => ms(a.entered_at) - ms(b.entered_at));
    let cur = null;
    for (const f of xs) {
        const again = !!cur && cur.state === 'APPLIED' && f.state === 'APPLIED' && /^ALREADY_ON_RECORD/.test(f.result_code ?? '');
        if (!again)
            cur = f;
    }
    return cur;
}
/** One anchor per record: with an approval entered after the record's first qualifying miss, the latest miss before it (the one it answered);
 *  otherwise the latest miss. A record is listed while one of its misses is within 30 days, or its approval is (I6: the window is the
 *  approval's), or `keep` names it (the controller's needs-a-person row, any age). Newest first. */
export function pickAnchors(requests, facts, now, keep = new Set()) {
    const by = new Map();
    for (const r of requests) {
        if (!qualifiesAny(r))
            continue;
        const k = k15(r.insuranceId);
        const a = by.get(k);
        if (a)
            a.push(r);
        else
            by.set(k, [r]);
    }
    const out = [];
    for (const [k, rs] of by) {
        rs.sort((a, b) => ms(a.at) - ms(b.at));
        const approval = approvalFor(facts, k, ms(rs[0].at));
        if (!(rs.some((r) => inWindow(r.at, now)) || (approval && inWindow(approval.entered_at, now)) || keep.has(k)))
            continue;
        const before = approval ? rs.filter((r) => ms(r.at) < ms(approval.entered_at)) : rs;
        const anchor = before[before.length - 1] ?? rs[rs.length - 1];
        out.push({ request: anchor, approval });
    }
    return out.sort((a, b) => ms(b.request.at) - ms(a.request.at));
}
/** the controller decided the approved number's call (its first, or its one retry): only then is a written number "call queued" (review of #105, N1) */
export function callDecided(row) {
    return row?.reason === 'APPROVED_CALL' || row?.reason === 'APPROVED_RETRY';
}
/** an action that never dialled (controller approved_call.js `used`) */
const UNUSED_ACTION = new Set(['HELD', 'RECONCILE_NONE', 'BLOCKED']);
/** what happened after the approval, newest truth first: a real claim on the record; the fact failed or still waits; the controller's
 *  needs-a-person row; the retry day (until the retry's action exists: final review 10-29); a staff edit before any call; a hold or the weekly cap
 *  (final review 10-29: "held: <reason>"); the second call just made; the latest call after the write; else written, call queued */
export function foundStatus(i, f) {
    if (i.record && realClaim(i.record.claim))
        return { kind: 'claim', claim: String(i.record.claim).trim() };
    if (f.state === 'FAILED')
        return { kind: 'not_written', code: f.result_code };
    if (f.state !== 'APPLIED')
        return { kind: 'approved', by: f.entered_by ?? 'the claims team', at: f.entered_at };
    const applied = ms(f.applied_at ?? f.entered_at);
    // Codex finding 3 on the fix branch: a request with no result one working day after it was made is no longer "running": a person checks the
    // call result, and it stops hiding the record's own row (foundHandled)
    const runningOr = (at) => (i.now.getTime() < oneWorkingDayAfter(ms(at)) ? 'running' : 'unresolved');
    const row = i.claimRow;
    const reason = row?.reason ?? null;
    // the words say how many calls were made (final review 10-29, deferred minor 1: a lost request goes to a person after ONE call): the approval's
    // requests after it, or its made actions when a request was never read, whichever is more
    if (reason === 'APPROVED_NEEDS_PERSON') {
        const made = Math.max(i.calls.filter((c) => ms(c.at) > applied).length, i.actions.filter((a) => (a.reason === 'APPROVED_CALL' || a.reason === 'APPROVED_RETRY') && !UNUSED_ACTION.has(a.state) && ms(a.created_at) > applied).length);
        return made >= 2 ? { kind: 'needs_person', calls: 2 } : made === 1 ? { kind: 'needs_person', calls: 1 } : { kind: 'needs_person' };
    }
    const retryAct = i.actions.filter((a) => a.reason === 'APPROVED_RETRY' && !UNUSED_ACTION.has(a.state) && ms(a.created_at) > applied)
        .sort((a, b) => ms(b.created_at) - ms(a.created_at))[0] ?? null;
    if (!retryAct && reason === 'APPROVED_RETRY' && row?.next_attempt_at && ms(row.next_attempt_at) > i.now.getTime())
        return { kind: 'second_call', on: row.next_attempt_at };
    const calls = i.calls.filter((c) => ms(c.at) > applied).sort((a, b) => ms(a.at) - ms(b.at));
    const firstAt = calls.length ? ms(calls[0].at) : Infinity;
    const staff = i.history.find((h) => !h.byAutomation && ms(h.at) > applied && ms(h.at) < firstAt);
    if (staff)
        return { kind: 'changed_by_staff', at: staff.at };
    if (row?.decision === 'HOLD' && reason)
        return { kind: 'held', reason, at: f.applied_at };
    if (row?.decision === 'WAIT' && reason === 'CAP_WEEKLY')
        return { kind: 'cap_wait', until: row.next_attempt_at };
    const last = calls[calls.length - 1];
    // the second call's request may not be read yet: the action is the truth that it was made (Salesforce CreatedDate is whole seconds)
    if (retryAct && (!last || ms(last.at) < ms(retryAct.created_at) - 1000))
        return { kind: 'called', at: retryAct.created_at, retry: true, result: runningOr(retryAct.created_at) };
    if (!last)
        return { kind: 'written', at: f.applied_at };
    const retry = (!!retryAct && ms(last.at) >= ms(retryAct.created_at) - 1000)
        || i.actions.some((a) => a.reason === 'APPROVED_RETRY' && !!a.created_can_id && k15(a.created_can_id) === k15(last.id));
    const result = last.disposition === 'Success' ? 'success' : last.disposition === 'Miss' ? failureReason({ Disposition__c: 'Miss', Miss_Reason__c: last.missReason }) : runningOr(last.at);
    return { kind: 'called', at: last.at, retry, result };
}
/** the finder's numbers (any document date) that differ from the record's current number and from what the AI told; null = the finder refused */
function differing(i) {
    if (!i.finder)
        return null;
    const pf = policyFindings(i.finder);
    if ('why' in pf)
        return null;
    const cur = policyNorm(i.record?.policy), told = policyNorm(i.request.told?.policy);
    return pf.found.filter((f) => f.norm !== cur && f.norm !== told);
}
/** each document (and the intake) that holds one of `found`, dated and linked: only a real document the number came from, never one we cannot name */
export function sourcesFor(documents, caseHref, found) {
    const out = [];
    const seen = new Set();
    for (const f of found) {
        if (f.origin === 'intake') {
            if (!seen.has(`intake|${f.norm}`)) {
                seen.add(`intake|${f.norm}`);
                out.push({ kind: 'intake', number: f.raw, at: null, href: caseHref, label: 'intake (on file now)' });
            }
            continue;
        }
        if (f.origin !== 'card' && f.origin !== 'dec_page' && f.origin !== 'police_report')
            continue;
        for (const d of documents) {
            if (d.source !== f.origin || policyNorm(d.policy) !== f.norm)
                continue;
            const k = `${d.source}|${f.norm}|${d.docAt ?? ''}`;
            if (seen.has(k))
                continue;
            seen.add(k);
            out.push({ kind: d.source, number: f.raw, at: d.docAt ?? null, href: d.href ?? null, label: d.label ?? DOC_LABEL[d.source] });
        }
    }
    return out.sort((a, b) => (ms(b.at) || 0) - (ms(a.at) || 0));
}
/** the gate's case facts: the case's claim numbers and every other record's policy (normalised) */
export function gateCtx(f) {
    return { claimKeys: f.claimKeys ?? new Set(), otherPolicies: new Set([...(f.sideRows ?? []), ...(f.otherSideRows ?? [])].map((r) => policyNorm(r.policyNumber)).filter((x) => !!x)) };
}
/** each distinct finder number against the record's: an offer (blank, near, different), refused with its reason, or the record's own (dropped).
 *  A blank verdict on a record that holds a value (the gate judged it junk) carries that value and why (gate review 10-29, rule a). */
export function offersOf(found, recordPolicy, ctx) {
    const offers = [];
    const rejected = [];
    const seen = new Set();
    const held = String(recordPolicy ?? '').trim();
    const junk = held ? wrongReason(held, null) : null;
    for (const f of found) {
        if (seen.has(f.norm))
            continue;
        seen.add(f.norm);
        const c = classifyPolicy(f.raw, recordPolicy, ctx);
        if (c.verdict === 'wrong')
            rejected.push({ number: f.raw, reason: c.reason });
        else if (c.verdict === 'blank' && junk)
            offers.push({ number: f.raw, verdict: c.verdict, reason: c.reason, recordWrong: { value: held, reason: junk } });
        else if (c.verdict !== 'exact')
            offers.push({ number: f.raw, verdict: c.verdict, reason: c.reason });
    }
    return { offers, rejected };
}
/** the numbers our files hold for THIS record's carrier (its party's police reports, its carrier's cards and dec pages, the intake) that the gate
 *  refuses: listed with the reason so nothing is dropped silently (a placeholder is noise, not listed) */
export function rejectedOf(f, documents, recordPolicy, ctx) {
    const carrier = f.row.carrierName || rowCarrierText(f.row.name);
    if (!carrier)
        return [];
    const party = f.side === '3P' ? 'DEF' : 'PLT';
    const mine = (c) => !!c && sameCarrier(String(c), String(carrier));
    const raws = [...documents.filter((d) => (d.source !== 'police_report' || d.party === party) && mine(d.carrier)).map((d) => d.policy),
        mine(f.intake?.carrier) ? f.intake?.policy ?? null : null];
    const out = new Map();
    for (const raw of raws) {
        if (!raw || !String(raw).trim())
            continue;
        const c = classifyPolicy(raw, recordPolicy, ctx);
        const k = c.norm ?? String(raw);
        if (c.verdict === 'wrong' && c.reason !== 'placeholder' && !out.has(k))
            out.set(k, { number: String(raw).trim(), reason: c.reason });
    }
    // Codex r1 (F4): a document linked to THIS record that names another carrier (the finder no longer takes it: an explicit contradiction) is listed
    // with the gate's own reason, the source's carrier context passed through (otherCarrier), so the chip says "it is from another carrier"
    const rowId15 = String(f.rowId ?? '').slice(0, 15);
    for (const d of documents) {
        if (!rowId15 || String(d.insuranceId ?? '').slice(0, 15) !== rowId15 || !d.policy || !String(d.policy).trim())
            continue;
        const named = rowCarrierText(d.carrier);
        if (!named || sameCarrier(String(named), String(carrier)))
            continue;
        const c = classifyPolicy(d.policy, recordPolicy, { ...ctx, otherCarrier: true });
        const k = c.norm ?? String(d.policy);
        if (c.verdict === 'wrong' && c.reason !== 'placeholder' && !out.has(k) && c.norm !== policyNorm(recordPolicy))
            out.set(k, { number: String(d.policy).trim(), reason: c.reason });
    }
    return [...out.values()];
}
/** an offer as a person reads it */
export function offerWords(o) {
    const how = o.verdict === 'blank' ? (o.recordWrong ? `the record holds ${o.recordWrong.value}, which is not a policy number (${WRONG_WORDS[o.recordWrong.reason]})` : 'the record has no number')
        : o.verdict === 'near' ? (o.reason === 'look_alike' ? "reads like the record's number (O/0, I/1, S/5, B/8)" : "one character off the record's number")
            : "differs from the record's number";
    return `${o.number}: ${how}`;
}
function blockOf(i) {
    const r = i.request;
    const b = (block, hold = null) => ({ block, hold });
    if (!i.record || !i.scope)
        return b('cant_check');
    if (realClaim(i.record.claim))
        return b('has_claim');
    if (!i.scope.inScope)
        return b('not_in_scope');
    if (i.scope.companionSecond)
        return b('companion');
    // no carrier Account on the record (record.carrier is the Account's name): the approved call goes to the carrier finder or parks (controller
    // decide.js, ASK CAP_FINDER) and nothing calls, so no Approve that promises a call (final review 10-29, deferred minor 3; the files lane's block)
    if (!i.record.carrier)
        return b('no_carrier');
    if (sweepStateFarm(i.record.carrier ?? r.carrier ?? ''))
        return b('state_farm');
    if (i.claimRow?.reason && FOUND_HOLDS.has(i.claimRow.reason))
        return b('controller_hold', i.claimRow.reason);
    if (i.facts.some((f) => f.kind === 'policy_number' && k15(f.insurance_id) === k15(r.insuranceId) && (f.state === 'PENDING' || f.state === 'APPLYING')))
        return b('fact_waiting');
    const later = i.history.filter((h) => ms(h.at) > ms(r.at)).sort((x, y) => ms(y.at) - ms(x.at))[0];
    if (later && later.byAutomation)
        return b('system_replaced');
    // Cesar 10-28 "3b": a person's number after the call blocks Approve only when our files back it too (both backed: a person decides)
    const cur = policyNorm(i.record.policy);
    if (later && cur && i.documents.some((d) => policyNorm(d.policy) === cur))
        return b('staff_changed');
    return b(null);
}
/** The record's row, or null when it is not on the list (no qualifying miss, no side, or no different number and no approval). */
export function foundRow(i) {
    const r = i.request;
    const side = sideOfRequest(r.side);
    if (!qualifiesAny(r) || !side || !r.matterId)
        return null;
    const needsPerson = i.claimRow?.reason === 'APPROVED_NEEDS_PERSON';
    if (!(inWindow(r.at, i.now) || (i.approval && inWindow(i.approval.entered_at, i.now)) || needsPerson))
        return null;
    const base = { lane: 'miss', current: i.record?.policy ?? null, canId: r.canId, canName: r.name, at: new Date(ms(r.at)).toISOString(), matterId: r.matterId, insuranceId: r.insuranceId, side,
        client: r.matter?.client ?? null, matterName: r.matter?.name ?? null, state: r.matter?.state ?? null, dol: r.matter?.dol ?? null, caseType: i.caseType ?? null, carrier: r.carrier, told: r.told?.policy ?? null, missReason: r.missReason,
        reason: r.reason };
    if (i.readFailed)
        return { ...base, found: [], sources: [], offers: [], rejected: [], offer: null, block: 'cant_check', hold: null, status: null, systemMaySwap: false, open: false, staffUnbacked: false, capWait: false };
    const diff = differing(i) ?? [];
    const ctx = i.finder ? gateCtx(i.finder) : {};
    const { offers: all, rejected: refused } = offersOf(diff, i.record?.policy ?? null, ctx);
    const rejected = i.finder ? [...refused, ...rejectedOf(i.finder, i.documents, i.record?.policy ?? null, ctx).filter((x) => !refused.some((y) => policyNorm(y.number) === policyNorm(x.number)))] : refused;
    const found = all.map((o) => o.number);
    const sources = sourcesFor(i.documents, i.caseHref, diff.filter((f) => found.some((n) => policyNorm(n) === f.norm)));
    // D2 (Cesar 10-29): the Queue group shows only what a person must act on; in flight and blocked rows show on the Today row and the chip
    const st = i.approval ? foundStatus(i, i.approval) : needsPerson ? { kind: 'needs_person' } : null;
    // "call queued" only once the controller decided the approved call (final review 10-29 I1: a decision the approved rule does not take, a carrier
    // the AI cannot call, must not promise one); until then the files lane's conditional words
    const status = st?.kind === 'written' && !callDecided(i.claimRow) ? { ...st, queued: false } : st;
    if (status)
        return { ...base, found, sources, offers: [], rejected, offer: null, block: null, hold: null, status, systemMaySwap: false,
            open: status.kind === 'needs_person' || status.kind === 'not_written', staffUnbacked: false, capWait: false };
    // I3 (fix round 1): only refused numbers: a chip-only row (and the Today strip) naming each with its reason; never listed or counted (D2). On an open
    // row (an offer too) the refused numbers show with the offer, in the Queue's approval strip as well (brief audit, Codex r1)
    if (!found.length)
        return rejected.length ? { ...base, found, sources, offers: [], rejected, offer: null, block: null, hold: null, status: null, systemMaySwap: false,
            open: false, staffUnbacked: false, capWait: false } : null;
    const { block, hold } = blockOf(i);
    const offers = block ? [] : all;
    const offer = offers[0]?.number ?? null;
    const norm = offer ? policyNorm(offer) : null;
    const swap = !!offer && offers.length === 1 && !!i.finder && (() => { const x = resolveBetterPolicy(i.finder, { nearRule: i.nearFix === true }); return x.status === 'REPLACE' && x.norm === norm; })();
    const cur = policyNorm(i.record?.policy);
    const staffUnbacked = !block && !!cur && i.history.some((h) => !h.byAutomation && ms(h.at) > ms(r.at)) && !i.documents.some((d) => policyNorm(d.policy) === cur);
    const capWait = !block && i.claimRow?.reason === 'CAP_WEEKLY';
    return { ...base, found, sources, offers, rejected, offer, block, hold, status: null, systemMaySwap: swap, open: !!offer, staffUnbacked, capWait };
}
/** Cesar 10-29: the Queue group lists, and its count counts, only the rows a person must act on (open): once Approve is pressed, the number is the
 *  record's, or the approved call is in flight or done, the row is off the Queue and out of every actionable number; its status stays on the case
 *  chip and the Today row. A needs-a-person row stays (a person must act). The one place the group's rows and count come from.
 *  Final review 10-29 (I5, D2 and Cesar's "drops from queue and is not messing up the actionable numbers"): while the Approve button is off
 *  (approveOn false) an Approve row is nothing a person can act on, so the group lists only the status rows a person acts on by opening the case
 *  (needs a person, not written); every other row stays read-only on the case chip and the Today row. */
export function foundQueueRows(rows, approveOn) {
    return rows.filter((r) => r.open && (approveOn || r.status?.kind === 'needs_person' || r.status?.kind === 'not_written'));
}
/** Final review 10-29 (I4): the records the Found-a-number group lists own their place in the Queue: every other Queue group drops them (core todo.ts
 *  buildTodo / buildQueue `owned`), so one record is in one group and counted once. Keyed on the record's id (15 characters), never on words; a
 *  record the group does not list (its read failed, no row, the button off, or a status row its board row takes: foundQueueTab) keeps its place in
 *  the other groups. listed: foundQueueRows' answer. */
export function foundOwned(listed) { return new Set(listed.map((r) => k15(r.insuranceId))); }
/** Codex r1 (F8), r2 (N3), narrowed by the review of #105 (N1): the records whose number a person entered and the system is still handling: the
 *  fact pending, its call running, its second call scheduled, the weekly-limit wait, or written with its approved call decided (`queued` not false:
 *  core callDecided). Their stale board row ("Enter the policy number", from the miss the number answered) leaves the Queue (core todo.ts, the
 *  policy group only), whichever Found rows are listed. A number written with no call decided (any typed policy number the controller does not
 *  call: `queued: false`) and a controller hold hide that row for at most one working day (Mon-Sat, Pacific: core oneWorkingDayAfter) after the
 *  write; then the board's row is back, so nobody's typed number leaves the record off the Queue. A later failure (a call that came back failed)
 *  or an explicit needs-a-person state (needs_person, not_written: listed by the Found group) is never handled here. A call still "running" hides
 *  it only until one working day after the call (Codex round 2 on the fix branch: a row kept from an earlier answer, when a refresh fails, says
 *  running for ever; the clock here is the page's own). */
export function foundHandled(rows, now) {
    const fresh = (at) => now.getTime() < oneWorkingDayAfter(ms(at));
    const handled = (s) => !!s && (s.kind === 'approved' || s.kind === 'second_call' || s.kind === 'cap_wait'
        || (s.kind === 'called' && s.result === 'running' && fresh(s.at)) || (s.kind === 'written' && (s.queued !== false || fresh(s.at))) || (s.kind === 'held' && fresh(s.at)));
    return new Set(rows.filter((r) => handled(r.status)).map((r) => k15(r.insuranceId)));
}
/** Codex r1 (F12): a Found row's search words: what its two lines show (the client, the case, its state, date of loss and case type, the carrier and
 *  side, what the AI told or the record holds, the numbers offered), the same kinds of words the board's case search reads */
export function foundSearchWords(r) {
    return [r.client, r.matterName, r.state, dolLabel(r.dol), r.caseType, r.carrier, r.side, r.told, r.current, ...r.offers.map((o) => o.number)];
}
/** Codex r1 (F12): the Queue's Found-a-number group under the page's narrowing (`keep`: the State row and the search): ONE predicate, applied before
 *  ownership, so the records the group shows are exactly the ones it owns. A record whose Found row the search hides keeps its board row (shown once
 *  when the board's own search matches it), never neither. */
export function foundQueueView(rows, approveOn, keep) {
    const shown = foundQueueRows(rows, approveOn).filter(keep);
    return { shown, owned: foundOwned(shown) };
}
/** One Queue tab's Found group against that tab's board rows (Codex on the fix branch, finding 1: one row and one count per record, across every
 *  group). A listed status-only row (a typed number that failed, or needs a person: no Approve, only "open the case") whose record has another
 *  actionable row on the tab (an issue row, not parked, past its first 15 minutes, in a group other than "Enter the policy number") is not listed:
 *  the record stays in that row's group, the row carries the status (deferred: core todo.ts TodoItem.foundStatus), and its policy row, if any,
 *  leaves the Queue. Otherwise the Found row lists the record and owns it (foundOwned). policy: the records whose policy row leaves the Queue (the
 *  handled ones, foundHandled, and the deferred ones). boardRows: the tab's rows under the same narrowing (the State row, the search). */
export function foundQueueTab(rows, approveOn, keep, boardRows, handled, now) {
    const other = new Set(boardRows.filter((r) => isIssueRow(r) && !isParked(r)).filter((r) => {
        const group = todoGroup(r);
        return group !== 'policy' && !inGrace({ group, row: r }, now);
    }).map((r) => k15(r.insuranceId)));
    const deferred = new Map();
    const shown = [];
    for (const r of foundQueueView(rows, approveOn, keep).shown) {
        const k = k15(r.insuranceId);
        if (!r.offers.length && r.status && other.has(k))
            deferred.set(k, r.status);
        else
            shown.push(r);
    }
    return { shown, owned: foundOwned(shown), policy: new Set([...handled, ...deferred.keys()]), deferred };
}
export const FOUND_BLOCK_WORDS = Object.freeze({
    cant_check: "Can't check right now",
    has_claim: 'The record already has a claim number',
    not_in_scope: 'The case is not in an active stage (for example termination or withdrawal), so the system does not write to it',
    companion: "Second client from the same crash: it stays under the main client's claim",
    state_farm: 'State Farm record: a new number can restart an online filing, so a person enters it',
    controller_hold: 'The system is holding this record',
    fact_waiting: 'A policy number entered for this record is still waiting to be written',
    system_replaced: 'The system already replaced the number after the call; its own call comes next',
    staff_changed: 'Staff changed the number after the call, and our files show that number too: check with them first',
    in_flight: 'A call for this record is in progress; its result comes first',
    // the files lane (Task 13 fix round 1, I1): the AI never calls a record with no carrier, or one whose carrier is not an insurer it can call
    no_carrier: 'No carrier is linked to the record, so the AI has no one to call: link the carrier first',
    not_a_carrier: "The record's carrier is a placeholder, not an insurer the AI can call: link the real carrier first",
    // final review 10-29 (deferred minor 6): a public agency is not a placeholder to replace; a claim against it is a government claim form
    public_agency: "The record's carrier is a public agency: a claim against it needs a government claim form, not a call, so a person files it",
    // Codex r2 (N5), r3 (R3-5): the generic words; foundBlockWords names the number and the document when the row carries them
    current_backed: "Our files show the record's number too, so the system will not replace it with another: check both numbers",
});
export function foundBlockWords(row) {
    if (!row.block)
        return null;
    if (row.block === 'current_backed' && row.current && row.backedBy) {
        return `Our ${row.backedBy} shows the record's number ${row.current}, so the system will not replace it with another: check both numbers`;
    }
    if (row.block === 'controller_hold' && row.hold)
        return `${FOUND_BLOCK_WORDS.controller_hold}: ${controllerReason('HOLD', row.hold).text}`;
    return FOUND_BLOCK_WORDS[row.block];
}
/** " (the second call)" and the outcome of a call; a helper so the template literal holds no `retry` field access */
function calledWords(s) {
    const r = s.result;
    const out = r === 'success' ? 'got a claim number' : r === 'running' ? 'calling now or waiting for its result' : r === 'unresolved' ? 'Check the call result: none came back' : FAILURE_TEXT[r];
    const second = s.retry ? ' (the second call)' : '';
    return `${second} · ${out}`;
}
export function foundStatusWords(s) {
    switch (s.kind) {
        case 'approved': return `Approved by ${s.by} · ${pacificStamp(s.at)}`;
        // queued false: no approved call is decided (a files-lane record, or the hard lock: core router_standdown.ts), so no word promises one (Codex r1 F9)
        case 'written': return s.queued === false ? 'Number written · any new call follows the usual rules' : 'Number written · call queued';
        case 'called': return `Called ${pacificStamp(s.at)}${calledWords(s)}`;
        case 'claim': return `Claim opened (${s.claim})`;
        case 'second_call': return `Not found · calling again on ${pacificDay(s.on)}`;
        case 'needs_person': return s.calls === 2 ? 'Called twice, still not found · needs a person'
            : s.calls === 1 ? 'Called once, no claim number came back · needs a person' : 'The approved number did not open a claim · needs a person';
        case 'changed_by_staff': return 'Number changed by staff before the call · nothing to do';
        case 'held': return `Held: ${controllerReason('HOLD', s.reason).text}`;
        case 'cap_wait': return `Waiting for this week's call limit to free${s.until ? ` · calls ${pacificDay(s.until)}` : ''}`;
        case 'not_written': return s.past ? 'Number not written' : `Not written: ${FACT_RESULT[String(s.code ?? '').replace(/_NO_NOTE$/, '')] ?? 'the system could not write it'}`;
    }
}
/** "Not used: N (reason) · M (reason)": the numbers the gate refuses, in plain words (null without one) */
export function rejectedWords(row) {
    return row.rejected.length ? `Not used: ${row.rejected.map((x) => `${x.number} (${WRONG_WORDS[x.reason]})`).join(' · ')}` : null;
}
/** the case chip: a person must act (need), the system is working (wait), or it is over (done). Fix round 1 (I2, I3): the chip is the only place a
 *  blocked or refused-only files-lane row shows, so its hover names every number our files hold, why there is no button, and each refusal. */
export function foundChip(row) {
    const s = row.status;
    const held = row.offers.length ? row.offers.map((o) => o.number) : row.found;
    const title = s ? foundStatusWords(s)
        : [held.length ? `Our files hold ${held.join(', ')}` : null, foundBlockWords(row), rejectedWords(row)].filter((x) => !!x).join(' · ');
    if (!s && !held.length)
        return { label: 'found number · not used', tone: 'done', title };
    if (!s)
        return { label: 'found a number', tone: 'need', title };
    if (s.kind === 'claim' || s.kind === 'changed_by_staff')
        return { label: 'found number · done', tone: 'done', title };
    if (s.kind === 'not_written' && s.past)
        return { label: 'found number · not written', tone: 'done', title };
    if (s.kind === 'needs_person' || s.kind === 'not_written')
        return { label: 'found number · needs a person', tone: 'need', title };
    if (s.kind === 'held')
        return { label: 'found number · held', tone: 'need', title };
    // Codex r1 (F9): "calling" only once a call is decided or made; a number still to be written, or written with no approved call decided (the hard
    // lock keeps the approved rule asleep), never says the AI calls
    if (s.kind === 'called' && s.result === 'unresolved')
        return { label: 'found number · check the call', tone: 'need', title };
    if (s.kind === 'approved')
        return { label: 'found number · writing', tone: 'wait', title };
    if (s.kind === 'written' && s.queued === false)
        return { label: 'found number · written', tone: 'wait', title };
    return { label: 'found number · calling', tone: 'wait', title };
}
//# sourceMappingURL=found_number.js.map